import {
  Dialog,
  Snackbar,
  Alert,
  Typography,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Button,
  DialogActions,
  useTheme,
  useMediaQuery,
  TextField,
  Collapse,
  Box,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {useEffect, useState, useCallback} from 'react';
import {AnimationLoadingScreenWithProgressBar} from '../loading/loadingScreen';
import {useCheckoutPaymentsApi} from '../../api/payment';
import {mainLinearGradient} from '../../theme/gradients';
import {onlyShowMonthlyPlanInPayments} from '../../constants';

/**
 * Helper function to calculate discounted price
 */
const calculatePrice = (originalPrice, discountInfo, period) => {
  if (!discountInfo || !discountInfo[period]) {
    return originalPrice;
  }
  return discountInfo[period].finalPrice;
};

/**
 * Helper function to get discount amount
 */
const getDiscountAmount = (discountInfo, period) => {
  if (!discountInfo || !discountInfo[period]) {
    return 0;
  }
  return discountInfo[period].discountAmount;
};

/**
 * ExtraAnnualText component for rendering additional text for annual plans.
 */
const ExtraAnnualText = ({isMobile, hasDiscount, discountInfo}) => {
  const originalYearlyPrice = 384;
  const discountedYearlyPrice = discountInfo?.yearly ? discountInfo.yearly.finalPrice * 12 : originalYearlyPrice;

  return (
    <>
      <Typography
        fontSize={18}
        fontWeight={700}
        sx={{
          lineHeight: isMobile ? 1.2 : undefined,
        }}
      >
        {hasDiscount && (
          <span
            style={{
              fontSize: '14px',
              textDecoration: 'line-through',
              color: '#6B7280',
              fontWeight: 400,
              marginRight: '8px',
            }}
          >
            ${originalYearlyPrice}
          </span>
        )}
        ${hasDiscount ? discountedYearlyPrice.toFixed(0) : originalYearlyPrice}
        <span
          style={{
            fontSize: '12px',
            fontWeight: 400,
          }}
        >
          {' '}
          /yr billed annually
        </span>
        <br />
        <span
          style={{
            display: !isMobile ? 'none' : 'normal',
            fontSize: '14px',
            color: '#31A1F3',
            fontWeight: 700,
          }}
        >
          save 27%
        </span>
      </Typography>
      <Typography
        color="#31A1F3"
        fontWeight={700}
        sx={{
          display: isMobile ? 'none' : 'normal',
        }}
      >
        save 27%
      </Typography>
    </>
  );
};

/**
 * CheckoutButton component for rendering the checkout button.
 */
const CheckoutButton = ({onClick, period, promoCode}) => {
  return (
    <Grid
      container
      item
      xs={4}
      md
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Button
        onClick={() => onClick(period, promoCode)}
        sx={{
          background: `linear-gradient(91.76deg, #31A6EE 0.45%, #35C6C9 53.85%, #A6E177 100%)`,
          borderColor: mainLinearGradient,
          color: 'white',
          borderRadius: 5,
          fontSize: 16,
          height: {
            xs: 'fit-content',
            sm: '100%',
          },
          fontWeight: 600,
          borderWidth: 3,
          px: 6,
        }}
      >
        Upgrade
      </Button>
    </Grid>
  );
};

/**
 * BenefitListItem component for rendering a list item with a benefit.
 */
const BenefitListItem = ({text}) => {
  return (
    <ListItem
      alignItems="center"
      disablePadding
      sx={{
        justifyContent: 'start',
        width: {
          xs: '100%',
          sm: '65%',
          md: '50%',
        },
        marginX: 'auto',
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 'fit-content !important',
          pr: 2,
        }}
      >
        <CheckIcon sx={{color: '#36C6CB'}} />
      </ListItemIcon>
      <ListItemText
        primary={text}
        sx={{
          flex: 'unset',
          display: 'inline-flex',
          width: 'fit-content !important',
          '& span': {
            width: 'fit-content',
          },
        }}
      />
    </ListItem>
  );
};

/**
 * PromoCodeSection component for handling promo code input
 */
const PromoCodeSection = ({
  promoCode,
  setPromoCode,
  promoApplied,
  setPromoApplied,
  promoError,
  setPromoError,
  isValidating,
  setIsValidating,
  onPromoApplied,
}) => {
  const [showPromoField, setShowPromoField] = useState(false);
  const theme = useTheme();
  const paymentsApi = useCheckoutPaymentsApi();

  const handlePromoCodeChange = event => {
    setPromoCode(event.target.value);
    setPromoError('');
    setPromoApplied(false);
    // Reset discount when promo code changes
    onPromoApplied(null);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsValidating(true);
    try {
      // Call the actual API to validate promo code and get pricing
      const monthlyResponse = await paymentsApi.postCheckout('monthly', promoCode);
      const yearlyResponse = await paymentsApi.postCheckout('yearly', promoCode);

      if (monthlyResponse.status === 200 && yearlyResponse.status === 200) {
        const discountInfo = {
          monthly: {
            originalPrice: monthlyResponse.data.original_price,
            finalPrice: monthlyResponse.data.final_price,
            discountAmount: monthlyResponse.data.discount_amount,
            currency: monthlyResponse.data.currency,
          },
          yearly: {
            originalPrice: yearlyResponse.data.original_price,
            finalPrice: yearlyResponse.data.final_price,
            discountAmount: yearlyResponse.data.discount_amount,
            currency: yearlyResponse.data.currency,
          },
        };

        setPromoApplied(true);
        setPromoError('');
        onPromoApplied(discountInfo);
      } else {
        throw new Error('Failed to validate promo code');
      }
    } catch (error) {
      // Handle the specific API error response format
      if (error.response && error.response.data && error.response.data.detail) {
        setPromoError(error.response.data.detail);
      } else {
        setPromoError('Error validating promo code. Please try again.');
      }
      setPromoApplied(false);
      onPromoApplied(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      validatePromoCode();
    }
  };

  return (
    <Box sx={{mb: 4, mx: 'auto', maxWidth: '600px'}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          p: 2,
          backgroundColor: '#F0F9FF',
          borderRadius: '12px',
          mb: 1,
          border: '1px solid #E0F2FE',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#E0F2FE',
          },
        }}
        onClick={() => setShowPromoField(!showPromoField)}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <LocalOfferIcon sx={{color: '#0891B2'}} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#0F172A',
            }}
          >
            Have a promo code?
          </Typography>
        </Box>
        <IconButton size="small" sx={{color: '#0891B2'}}>
          {showPromoField ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={showPromoField}>
        <Box
          sx={{
            p: 3,
            backgroundColor: '#FAFBFC',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            mb: 2,
          }}
        >
          <Box sx={{display: 'flex', gap: 2, mb: 2}}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={handlePromoCodeChange}
              onKeyPress={handleKeyPress}
              error={!!promoError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={validatePromoCode}
              disabled={!promoCode.trim() || isValidating}
              sx={{
                minWidth: '120px',
                px: 3,
                borderRadius: '8px',
                background: 'linear-gradient(91.76deg, #31A6EE 0.45%, #35C6C9 53.85%, #A6E177 100%)',
                '&:hover': {
                  background: 'linear-gradient(91.76deg, #2563EB 0.45%, #2DD4BF 53.85%, #84CC16 100%)',
                },
                '&:disabled': {
                  background: '#E2E8F0',
                },
                color: 'white',
                fontWeight: 600,
              }}
            >
              {isValidating ? <CircularProgress size={20} sx={{color: 'white'}} /> : 'Apply'}
            </Button>
          </Box>

          {promoError && (
            <Alert severity="error" sx={{mb: 1, borderRadius: '8px'}}>
              {promoError}
            </Alert>
          )}

          {promoApplied && (
            <Alert severity="success" sx={{mb: 1, borderRadius: '8px'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="body2">Promo code applied successfully! Prices updated below.</Typography>
                <Chip label={promoCode} size="small" color="success" variant="outlined" />
              </Box>
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const cardContainerStyles = {
  p: 2,
  borderRadius: 5,
  backgroundColor: '#F8FAFC',
  border: '4px solid transparent',
  boxShadow: '0px 6px 8px 3px rgba(0, 0, 0, 0.1);',
  alignContent: 'space-between',
  justifyContent: {xs: 'space-evenly', sm: 'center'},
  textAlign: 'center',
};

/**
 * PricingPlansPopup component for rendering a pricing plans popup with promo code support.
 */
export const PricingPlansPopup = ({open, setOpen}) => {
  const [waitingCheckoutLink, setWaitingCheckoutLink] = useState(false);
  const [responseReturned, setResponseReturned] = useState(false);
  const [message, setMessage] = useState(undefined);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);

  const paymentsApi = useCheckoutPaymentsApi();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const theme = useTheme();

  // Handle promo code application
  const handlePromoApplied = useCallback(newDiscountInfo => {
    setDiscountInfo(newDiscountInfo);
  }, []);

  // Calculate prices with discount
  const getMonthlyPrice = useCallback(() => {
    const originalPrice = 44;
    return calculatePrice(originalPrice, discountInfo, 'monthly');
  }, [discountInfo]);

  const getYearlyPrice = useCallback(() => {
    const originalPrice = 32;
    return calculatePrice(originalPrice, discountInfo, 'yearly');
  }, [discountInfo]);

  const getMonthlyDiscount = useCallback(() => {
    return getDiscountAmount(discountInfo, 'monthly');
  }, [discountInfo]);

  const getYearlyDiscount = useCallback(() => {
    return getDiscountAmount(discountInfo, 'yearly');
  }, [discountInfo]);

  const handleCheckout = async (period, appliedPromoCode = '') => {
    setWaitingCheckoutLink(true);

    try {
      // Use the promo code if it's applied, otherwise use empty string
      const finalPromoCode = promoApplied ? appliedPromoCode : '';
      const checkoutLinkReq = await paymentsApi.postCheckout(period, finalPromoCode);

      if (checkoutLinkReq.status !== 200) {
        setResponseReturned(true);
        setOpen(false);
        setWaitingCheckoutLink(false);
        setMessage('An error occurred. Please try later.');
        return;
      }

      const link = checkoutLinkReq.data.redirect_url;
      window.location.href = link;
    } catch (error) {
      console.error('Error during checkout:', error);
      setResponseReturned(true);
      setOpen(false);
      setWaitingCheckoutLink(false);

      // Handle specific API error responses
      if (error.response && error.response.data && error.response.data.detail) {
        setMessage(error.response.data.detail);
      } else {
        setMessage('An error occurred. Please try later.');
      }
    }
  };

  // Reset states when dialog opens
  useEffect(() => {
    if (open) {
      setPromoCode('');
      setPromoApplied(false);
      setPromoError('');
      setIsValidating(false);
      setDiscountInfo(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !onlyShowMonthlyPlanInPayments) return;
    handleCheckout('monthly');
  }, [open]);

  return (waitingCheckoutLink || onlyShowMonthlyPlanInPayments) && open ? (
    <AnimationLoadingScreenWithProgressBar title="Loading..." secondsToLoad={30} isAlmostComplete={responseReturned} />
  ) : (
    <>
      <Dialog
        maxWidth="md"
        fullWidth
        open={open}
        onClose={() => {
          setResponseReturned(true);
          setOpen(false);
        }}
        sx={{
          '& .MuiDialog-paperScrollPaper': {
            borderRadius: 5,
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant={'h4'}
            textAlign="center"
            sx={{
              pt: {
                xs: 3,
                sm: undefined,
              },
              fontSize: {
                xs: 28,
                sm: undefined,
              },
            }}
          >
            Select your plan
          </Typography>
        </DialogTitle>
        <DialogContent sx={{px: {xs: 2, sm: 5}}}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{
              textAlign: 'center',
            }}
          >
            Benefits of premium plans:
          </Typography>
          <List dense={isSmallScreen}>
            <BenefitListItem text={'Basic symptom screening'} />
            <BenefitListItem text={'20 health metrics face scan'} />
            <BenefitListItem text={'Upgrade to advance symptom screening'} />
            <BenefitListItem text={'Track your health progress (coming soon)'} />
          </List>

          {/* Promo Code Section */}
          <PromoCodeSection
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            promoApplied={promoApplied}
            setPromoApplied={setPromoApplied}
            promoError={promoError}
            setPromoError={setPromoError}
            isValidating={isValidating}
            setIsValidating={setIsValidating}
            onPromoApplied={handlePromoApplied}
          />

          <Grid container gap={4} mt={2}>
            <Grid container item xs sx={{...cardContainerStyles, position: 'relative'}}>
              {/* Promo Applied Indicator for Monthly */}
              {promoApplied && getMonthlyDiscount() > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    zIndex: 10,
                  }}
                >
                  <Chip
                    label={`-$${getMonthlyDiscount().toFixed(2)}`}
                    color="success"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 2,
                      },
                    }}
                  />
                </Box>
              )}

              <Grid item xs={6} sm={12}>
                <Typography fontSize={{xs: 22, sm: 30}} fontWeight={800}>
                  Monthly
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap'}}>
                  {/* Show original price crossed out if there's a discount */}
                  {promoApplied && getMonthlyDiscount() > 0 && (
                    <Typography
                      fontSize={{xs: 16, sm: 20}}
                      sx={{
                        textDecoration: 'line-through',
                        color: '#6B7280',
                        fontWeight: 400,
                      }}
                    >
                      $44/mo
                    </Typography>
                  )}
                  <Typography fontSize={{xs: 18, sm: 26}} fontWeight={800}>
                    ${getMonthlyPrice()}/mo
                  </Typography>
                </Box>
                {promoApplied && getMonthlyDiscount() > 0 && (
                  <Chip label="Discount Applied" size="small" color="success" sx={{mt: 1}} />
                )}
              </Grid>
              <CheckoutButton onClick={handleCheckout} period="monthly" promoCode={promoCode} />
            </Grid>

            <Grid container item xs sx={{...cardContainerStyles, position: 'relative'}}>
              {/* Promo Applied Indicator for Annual */}
              {promoApplied && getYearlyDiscount() > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    zIndex: 10,
                  }}
                >
                  <Chip
                    label={`-$${getYearlyDiscount().toFixed(2)}`}
                    color="success"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: 2,
                      },
                    }}
                  />
                </Box>
              )}

              <Grid item xs={6} sm={12} mb={{xs: 0, sm: 3}}>
                <Typography fontSize={{xs: 22, sm: 30}} fontWeight={800}>
                  Annual
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap'}}>
                  {/* Show original price crossed out if there's a discount */}
                  {promoApplied && getYearlyDiscount() > 0 && (
                    <Typography
                      fontSize={{xs: 16, sm: 20}}
                      sx={{
                        textDecoration: 'line-through',
                        color: '#6B7280',
                        fontWeight: 400,
                      }}
                    >
                      $32/mo
                    </Typography>
                  )}
                  <Typography fontSize={{xs: 18, sm: 26}} fontWeight={800}>
                    ${getYearlyPrice()}/mo
                  </Typography>
                </Box>
                {!isSmallScreen ? (
                  <ExtraAnnualText hasDiscount={promoApplied && getYearlyDiscount() > 0} discountInfo={discountInfo} />
                ) : (
                  ''
                )}
                {promoApplied && getYearlyDiscount() > 0 && (
                  <Chip label="Discount Applied" size="small" color="success" sx={{mt: 1}} />
                )}
              </Grid>
              <CheckoutButton onClick={handleCheckout} period="yearly" promoCode={promoCode} />
              <Grid
                item
                xs={12}
                sx={{
                  display: {xs: 'flex', sm: 'none'},
                  flexDirection: 'column',
                  alignItems: 'start',
                }}
              >
                <ExtraAnnualText
                  isMobile
                  hasDiscount={promoApplied && getYearlyDiscount() > 0}
                  discountInfo={discountInfo}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            paddingBottom: theme.spacing(2),
            px: {
              xs: theme.spacing(3.8),
              sm: theme.spacing(1.7),
            },
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 9,
          }}
        >
          <Button
            variant="text"
            onClick={() => {
              setResponseReturned(true);
              setOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={2000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={message !== undefined}
        onClose={() => {
          setMessage(undefined);
        }}
      >
        <Alert
          severity="error"
          sx={{
            mt: 0.5,
            mb: 6,
            alignSelf: 'center',
            width: '90%',
            textAlign: 'center',
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};
